/**
 * Bouton "Générer une chasse aux indices (IA)" monté sur la carte d'ÉDITION
 * d'une question. Appelle l'edge fn `exercise-v2-clue-hunt` sur cette question
 * précise (via question_index) et applique les ops. Si le LLM juge la chasse
 * non pertinente ici, on affiche un message discret.
 *
 * Mobile-first : bouton `w-full` sur < sm, min-h 44px, feedback tactile.
 */

import { useMemo, useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { useClueHuntPhase } from '@/lib/exercise-v2/use-v2-generation';
import type { ExerciseBlock } from '@/lib/exercise-v2/types';

export function GenerateClueHuntButton({
  exerciseId,
  question,
  allBlocks,
}: {
  exerciseId: string;
  question: ExerciseBlock;
  allBlocks: ExerciseBlock[];
}) {
  const phase = useClueHuntPhase(exerciseId);
  const [message, setMessage] = useState<string | null>(null);

  // Résolution de l'index 0-based de la question dans l'ordre des questions.
  const questionIndex = useMemo(() => {
    const ordered = allBlocks
      .filter((b) => b.section === 'statement' && b.block_type === 'statement_question')
      .sort((a, b) => a.position - b.position);
    return ordered.findIndex((q) => q.id === question.id);
  }, [allBlocks, question.id]);

  async function trigger() {
    setMessage(null);
    try {
      const res = await phase.mutateAsync({ questionIndex });
      if (res.no_clues_emitted) {
        setMessage("Le LLM n'a pas trouvé de chasse aux indices pertinente pour cette question.");
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : String(err));
    }
  }

  const busy = phase.isPending;

  return (
    <div className="mt-2 space-y-1.5 border-t border-slate-100 pt-2">
      <button
        type="button"
        onClick={trigger}
        disabled={busy || questionIndex < 0}
        className={cn(
          'flex min-h-11 w-full items-center justify-center gap-1.5 rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-xs font-medium text-cyan-800 active:scale-[0.98] sm:w-auto',
          busy && 'opacity-60',
        )}
      >
        {busy ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Sparkles className="h-3.5 w-3.5" />
        )}
        {busy ? 'Génération…' : 'Générer une chasse aux indices (IA)'}
      </button>
      {message && <p className="text-[11px] text-slate-500">{message}</p>}
    </div>
  );
}
